import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isAxiosError } from 'axios';

import type {
  InviteEligibilityApplicant,
  InviteEligibilityResponseData,
} from '@/modules/application-tracker/api';

import { InviteLeaseDetailsSegmentEvents } from '../constants';
import {
  recordInviteEligibilityBlockShown,
  recordInviteEligibilityPreflightCompleted,
  recordInviteEligibilityPreflightUnavailable,
  recordInviteEligibilityRecordLinkClicked,
} from '../invite-eligibility.rum';
import {
  summarizeInviteEligibilityPreflight,
  type InviteEligibilityFlow,
} from '../utils/invite-eligibility-telemetry.util';
import type { InviteEligibilityPreflightState } from './use-invite-eligibility-check';
import { useInviteTracking } from './use-invite-tracking';

/** Edit invite passes its own invite id to the preflight; create invite has none. */
export const inviteEligibilityFlow = (inviteAlphaId: string | undefined): InviteEligibilityFlow =>
  inviteAlphaId ? 'edit' : 'create';

const httpStatusOf = (error: unknown): number | undefined =>
  isAxiosError(error) ? error.response?.status : undefined;

/**
 * Segment + RUM for the eligibility preflight. Properties carry counts and
 * outcomes only, never renter emails, record ids or addresses.
 */
export function useInviteEligibilityTracking(flow: InviteEligibilityFlow) {
  const { trackInvite } = useInviteTracking();

  const trackPreflightRequested = useCallback(
    (renterCount: number) => {
      trackInvite(InviteLeaseDetailsSegmentEvents.EligibilityPreflightRequested, {
        invite_flow: flow,
        renter_count: renterCount,
      });
    },
    [trackInvite, flow],
  );

  const trackPreflightCompleted = useCallback(
    (data: InviteEligibilityResponseData) => {
      const summary = summarizeInviteEligibilityPreflight(data);
      trackInvite(InviteLeaseDetailsSegmentEvents.EligibilityPreflightCompleted, {
        invite_flow: flow,
        ...summary,
      });
      recordInviteEligibilityPreflightCompleted(flow, summary);
    },
    [trackInvite, flow],
  );

  const trackPreflightUnavailable = useCallback(
    (renterCount: number, httpStatus?: number) => {
      trackInvite(InviteLeaseDetailsSegmentEvents.EligibilityPreflightCompleted, {
        invite_flow: flow,
        decision: 'unavailable',
        renter_count: renterCount,
        ...(httpStatus ? { http_status: httpStatus } : {}),
      });
      recordInviteEligibilityPreflightUnavailable(flow, renterCount, httpStatus);
    },
    [trackInvite, flow],
  );

  const trackBlockShown = useCallback(
    (blockCount: number) => {
      trackInvite(InviteLeaseDetailsSegmentEvents.EligibilityBlockShown, {
        invite_flow: flow,
        block_count: blockCount,
      });
      recordInviteEligibilityBlockShown(flow, blockCount);
    },
    [trackInvite, flow],
  );

  const trackRecordLinkClicked = useCallback(
    (applicant: InviteEligibilityApplicant) => {
      trackInvite(InviteLeaseDetailsSegmentEvents.EligibilityRecordLinkClicked, {
        invite_flow: flow,
        outcome: applicant.outcome,
        match_count: applicant.total_matches,
      });
      recordInviteEligibilityRecordLinkClicked(flow, applicant.outcome, applicant.total_matches);
    },
    [trackInvite, flow],
  );

  return useMemo(
    () => ({
      trackPreflightRequested,
      trackPreflightCompleted,
      trackPreflightUnavailable,
      trackBlockShown,
      trackRecordLinkClicked,
    }),
    [
      trackPreflightRequested,
      trackPreflightCompleted,
      trackPreflightUnavailable,
      trackBlockShown,
      trackRecordLinkClicked,
    ],
  );
}

type UseTrackInviteEligibilityPreflightArgs = {
  flow: InviteEligibilityFlow;
  preflight: InviteEligibilityPreflightState;
  blockCount: number;
};

/**
 * Mount once per form (the eligibility controller). Each network fetch gives
 * one Requested and one Completed event; Block Shown fires when a block appears.
 */
export function useTrackInviteEligibilityPreflight({
  flow,
  preflight,
  blockCount,
}: UseTrackInviteEligibilityPreflightArgs) {
  const {
    trackPreflightRequested,
    trackPreflightCompleted,
    trackPreflightUnavailable,
    trackBlockShown,
  } = useInviteEligibilityTracking(flow);
  const { fetchStatus, status, data, error, renterCount } = preflight;
  const isFetchingRef = useRef(false);
  const wasBlockShownRef = useRef(false);

  useEffect(() => {
    if (fetchStatus === 'fetching') {
      if (!isFetchingRef.current) {
        isFetchingRef.current = true;
        trackPreflightRequested(renterCount);
      }
      return;
    }

    if (!isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = false;

    if (status === 'error') {
      trackPreflightUnavailable(renterCount, httpStatusOf(error));
    } else if (status === 'success' && data) {
      trackPreflightCompleted(data);
    }
  }, [
    fetchStatus,
    status,
    data,
    error,
    renterCount,
    trackPreflightRequested,
    trackPreflightCompleted,
    trackPreflightUnavailable,
  ]);

  useEffect(() => {
    const isBlockShown = blockCount > 0;
    if (isBlockShown && !wasBlockShownRef.current) {
      trackBlockShown(blockCount);
    }
    wasBlockShownRef.current = isBlockShown;
  }, [blockCount, trackBlockShown]);
}
